import React, { useEffect, useState } from "react";
import { Calendar } from "primereact/calendar";
import {
  assetTypeOption,
  assetTypePensionOption,
  assetTypeRealEstateOption,
  assetTypeStockOption,
  companyAssetTypeOption,
  currencyOption,
  itemType,
  owernershipTypeOption,
  sectorss,
} from "../../../../utility/data";
import {
  FormDropdown,
  InputBorderBottomBorder,
  InputField,
  InputFieldWithOutBorder,
  NumberField,
  NumberFieldBorder,
} from "../../../../components/Stepper/fields/inputField";
import {
  formatNumber,
  limitToTwoDecimalPlaces,
  preventNegativeValue,
} from "../../../../utility/helper";
import { useDispatch, useSelector } from "react-redux";
import { getSymbolServices } from "../../../../services/addWealth/assetApi";
import { useNavigate } from "react-router-dom";
import { handleErrorRedirect } from "../../../../utility/errorRedirect";
import {
  fetchLegacies,
  resetLegaciesState,
} from "../../../../redux/slices/legacySlice";

const AssetForm = ({
  assetType,
  onSubmit,
  groupsData,
  userAssets,
  setLoading,
  loading,
}) => {
  const assetTypeMapping = {
    Stock: assetTypeStockOption[0].value,
    "Real Estate": assetTypeRealEstateOption[0].value,
    "Luxury Asset": assetTypeOption.find((opt) => opt.name === "Luxury Asset")
      ?.value,
    Pension: assetTypePensionOption[0].value,
    Company: assetTypeOption.find((opt) => opt.name === "Business")?.value,
    Cash: assetTypeOption.find((opt) => opt.name === "Liquidity")?.value,
  };
  const { currency } = useSelector((state) => state.user);
  const [sectors, setSectors] = useState(sectorss);
  const { legaciesData, legacyLoading } = useSelector(
    (state) => state.legacies
  );
  const [assetData, setAssetData] = useState({
    isin: "",
    name: "",
    inclusion: groupsData && groupsData[0],
    assetType: assetTypeMapping[assetType?.name] || assetTypeOption[0]?.value,
    currency: currency ? currency : currencyOption[0].value,
    sector:
      assetType?.name === "Stock"
        ? ""
        : assetType?.name === "Real Estate"
        ? sectors[5].value
        : assetType?.name === "Luxury Asset"
        ? sectors[1].value
        : sectors[0].value,
    linkedWith: "",
    ownership: "",
    ownershipType:
      assetType?.name === "Company" ? owernershipTypeOption[0].value : "",
    commitment: "",
    initialBalance: "",
    quantity: "",
    costPrice: "",
    noi: "",
    capRate: "",
    legacies: legaciesData.length > 0 ? legaciesData[0] : "",
    itemType: "",
    ebitda: "",
    multiples: "",
    date: new Date(),
    assetTypeId: assetType?.id,
    symbol: "",
    cashAssets: "",
    companyAssetType:
      assetType?.name === "Company" ? companyAssetTypeOption[0].value : "",
    age: "",
    retirement: "65",
    growthGrossSalary: "",
    maturityGrossSalary: "",
    annualReturn: "3",
    growth: "",
    maturity: "",
    conversionRate: "6",
    contributionYear: "44",
    secondPillarAverageIncome: "",
    thirdPillarAverageIncome: "",
    contributionRate: "10.00",
    AVSContribution: "10.55",
    futureContribution3rdPillar: "7'056",
    pastContribution3rdPillar: "",
  });
  const [errors, setErrors] = useState({});
  const [stockSymbol, setStockSymbol] = useState([]);
  const [linkedWith, setLinkedWith] = useState([]);
  const accessToken = JSON.parse(localStorage.getItem("access_token"));
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    if (accessToken) {
      dispatch(fetchLegacies(accessToken)).then((resultAction) => {
        if (fetchLegacies.rejected.match(resultAction)) {
          const status = resultAction?.payload?.status;
          handleErrorRedirect(status, navigate);
        }
      });
    }
    return () => {
      dispatch(resetLegaciesState());
    };
  }, [dispatch, accessToken]);

  useEffect(() => {
    if (currency && currency !== assetData.currency) {
      setAssetData((prevData) => ({
        ...prevData,
        currency,
      }));
    }
  }, [currency]);

  useEffect(() => {
    if (assetType?.name === "Stock" && assetData.isin) {
      const handler = setTimeout(async () => {
        await GetStockSymbolData(assetData.isin);
      }, 200);

      return () => {
        clearTimeout(handler);
      };
    }
  }, [assetData.isin, assetType?.name]);

  const getSectorValue = (sectorName) => {
    const sector = sectors.find((s) => s.name === sectorName);

    if (!sector) {
      const newSector = { name: sectorName, value: sectorName };
      setSectors((prevSectors) => [...prevSectors, newSector]);
      return newSector.value;
    }

    return sector.value;
  };

  useEffect(() => {
    if (groupsData && groupsData.length > 0 && !assetData.inclusion) {
      setAssetData((prev) => ({
        ...prev,
        inclusion: groupsData[0],
      }));
    }
  }, [groupsData]);

  useEffect(() => {
    const { growth, growthGrossSalary, maturity, maturityGrossSalary } =
      assetData;

    const cleanValue = (val) =>
      parseFloat(val?.toString().replace(/'/g, "")) || 0;

    const g = cleanValue(growth);
    const gg = cleanValue(growthGrossSalary);
    const m = cleanValue(maturity);
    const mg = cleanValue(maturityGrossSalary);

    const totalWeight = g + m;

    const isAnyFieldFilled =
      growth !== "" ||
      growthGrossSalary !== "" ||
      maturity !== "" ||
      maturityGrossSalary !== "";

    if (isAnyFieldFilled && totalWeight > 0) {
      const averageIncome = (g * gg + m * mg) / totalWeight;

      const formattedAvgIncome = formatNumber(Number(averageIncome.toFixed(2)));

      setAssetData((prev) => ({
        ...prev,
        secondPillarAverageIncome: formattedAvgIncome,
        thirdPillarAverageIncome: formattedAvgIncome,
      }));
    }
  }, [
    assetData.growth,
    assetData.growthGrossSalary,
    assetData.maturity,
    assetData.maturityGrossSalary,
  ]);

  useEffect(() => {
    const { age, retirement } = assetData;

    const cleanValue = (val) =>
      parseFloat(val?.toString().replace(/'/g, "")) || 0;

    const r = cleanValue(retirement);
    const a = cleanValue(age);

    if (r && a) {
      if (r <= a) {
        setErrors((prev) => ({
          ...prev,
          retirement: "Retirement age must be greater than current age.",
        }));
      } else {
        setErrors((prev) => ({
          ...prev,
          retirement: "",
        }));

        const maturityValue = formatNumber(Number((r - a).toFixed(2)));
        setAssetData((prev) => ({
          ...prev,
          maturity: maturityValue,
        }));
      }
    }
  }, [assetData.age, assetData.retirement]);

  useEffect(() => {
    if (assetType?.name === "Stock" && stockSymbol.length > 0) {
      const sectorValue = getSectorValue(stockSymbol[0].sector);

      setErrors((prevErrors) => ({
        ...prevErrors,
        name: "",
        symbol: "",
        sector: "",
      }));

      setAssetData((prevData) => ({
        ...prevData,
        name: stockSymbol[0]?.companyName,
        symbol: stockSymbol[0] ? stockSymbol[0] : "",
        sector: sectorValue,
      }));
    }
  }, [stockSymbol, assetType?.name]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (
        stockSymbol.length === 0 &&
        assetType?.name === "Stock" &&
        !assetData.isin
      ) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          isin: "",
        }));
      } else if (assetType?.name === "Stock" && !assetData.isin) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          isin: "Please enter the ISIN.",
        }));
      } else if (
        stockSymbol.length === 0 &&
        assetType?.name === "Stock" &&
        assetData.isin
      ) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          isin: "No data found for this ISIN.",
        }));
      } else {
        setErrors((prevErrors) => ({
          ...prevErrors,
          isin: "",
        }));
      }
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [stockSymbol, assetType, assetData]);

  const GetStockSymbolData = async (searchterms) => {
    try {
      const response = await getSymbolServices(searchterms, accessToken);
      if (response.status === 200) {
        const data = response?.data.data;
        setStockSymbol(data);
      } else {
        throw new Error("Failed to fetch user data");
      }
    } catch (err) {
      const status = err?.response?.status;
      handleErrorRedirect(status, navigate);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e, fieldName) => {
    let name, value;

    if (fieldName) {
      name = fieldName;
      value = e;
    } else if (e.target) {
      name = e.target.name;
      value = e.target.value;
    }
    if (
      (name === "quantity" || name === "costPrice") &&
      !preventNegativeValue(value)
    ) {
      return;
    }

    if (name === "initialBalance" && !limitToTwoDecimalPlaces(value)) {
      return;
    }

    setAssetData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
    }
  };

  const handleNumericChange = (e, fieldName) => {
    let value = e.target.value;

    const rawValue = value.replace(/[^0-9.,]/g, "").replace(/[,']/g, "");

    if (rawValue === "" || isNaN(rawValue)) {
      setAssetData((prevData) => ({
        ...prevData,
        [fieldName]: "",
      }));
      return;
    }

    if (!limitToTwoDecimalPlaces(rawValue)) return;

    if (rawValue.length > 12) return;

    setAssetData((prevData) => ({
      ...prevData,
      [fieldName]: formatNumber(rawValue),
    }));

    if (errors[fieldName]) {
      setErrors((prevErrors) => ({ ...prevErrors, [fieldName]: "" }));
    }
  };

  const handleDynamicNumericChange = (e, fieldName) => {
    let value = e.target.value;
    const allowDecimalFields = ["AVSContribution", "contributionRate", "conversionRate"];

    let rawValue;
    if (allowDecimalFields.includes(fieldName)) {
      rawValue = value.replace(/[^0-9.]/g, "");
      const parts = rawValue.split(".");
      if (parts.length > 2) rawValue = parts[0] + "." + parts[1];
      if (parts[1] && parts[1].length > 2) {
        rawValue = parts[0] + "." + parts[1].slice(0, 2);
      }
    } else {
      rawValue = value.replace(/[^0-9]/g, "");
    }

    if (rawValue === "" || isNaN(rawValue)) {
      setAssetData((prevData) => ({
        ...prevData,
        [fieldName]: "",
      }));
      return;
    }

    if (rawValue.length > 12) return;

    setAssetData((prevData) => ({
      ...prevData,
      [fieldName]: allowDecimalFields.includes(fieldName)
        ? rawValue
        : formatNumber(rawValue),
    }));

    if (errors[fieldName]) {
      setErrors((prevErrors) => ({ ...prevErrors, [fieldName]: "" }));
    }
  };

  // const handleNumericDecimalChange = (e, fieldName) => {
  //   const input = e.target;
  //   const rawValue = input.value;
  //   const selectionStart = input.selectionStart;

  //   // Remove formatting to get raw numeric string
  //   const rawNumeric = rawValue.replace(/[^0-9.\-]/g, "");

  //   // Format new value
  //   const formatted = formatFlexibleDecimalNumber(rawValue);

  //   setAssetData((prev) => ({
  //     ...prev,
  //     [fieldName]: formatted,
  //   }));

  //   // Delay restoring cursor position
  //   setTimeout(() => {
  //     const inputElement = document.querySelector(`input[name="${fieldName}"]`);
  //     if (inputElement) {
  //       const newLength = formatted.length;
  //       const diff = newLength - rawValue.length;
  //       const newCursor = Math.max(selectionStart + diff, 0);
  //       inputElement.setSelectionRange(newCursor, newCursor);
  //     }
  //   }, 0);
  // };

  const validateFields = () => {
    let newErrors = {};
    if (assetType?.name === "Stock" && !assetData.isin)
      newErrors.isin = "Please enter the ISIN.";
    if (!assetData.name) newErrors.name = "Enter your asset name.";
    if (!assetData.inclusion)
      newErrors.inclusion = "Inclusion details are required";
    if (!assetData.assetType)
      newErrors.assetType = "Please select the asset type.";
    if (!assetData.currency) newErrors.currency = "Please enter the currency.";
    if (!assetData.sector) newErrors.sector = "Please select a sector.";
    if (assetType?.name === "Company" && !assetData.ownership)
      newErrors.ownership = "Please enter the ownership.";
    if (assetType?.name === "Other" && !assetData.commitment)
      newErrors.commitment = "Please enter the commitment.";
    if (!assetData.date) newErrors.date = "Please select the date.";
    if (assetType?.name == "Stock" && !assetData.symbol)
      newErrors.symbol = "Please select the symbol.";
    if (
      assetType?.name !== "Stock" &&
      assetType?.name !== "Pension" &&
      !assetData.initialBalance
    )
      newErrors.initialBalance =
        assetType?.name == "Real Estate"
          ? "Please enter the Initial Purchase"
          : "Please enter the initial balance.";
    if (assetType?.name === "Stock" && !assetData.quantity)
      newErrors.quantity = "Please enter the quantity.";
    if (assetType?.name === "Stock" && !assetData.costPrice)
      newErrors.costPrice = "Please enter the cost price.";
    if (assetType?.name === "Real Estate" && !assetData.noi)
      newErrors.noi = "Please enter the NOI.";
    if (assetType?.name === "Real Estate" && !assetData.capRate)
      newErrors.capRate = "Please enter the cap rate.";
    if (assetType?.name === "Company" && !assetData.ebitda)
      newErrors.ebitda = "Please enter the EBITDA.";
    if (assetType?.name === "Company" && !assetData.multiples)
      newErrors.multiples = "Please enter the multiples.";
    if (assetType?.name === "Company" && !assetData.companyAssetType)
      newErrors.companyAssetType = "Please select the company Asset Type.";
    if (assetType?.name === "Company" && !assetData.ownershipType)
      newErrors.ownershipType = "Please select the ownership Type.";
    if (assetType?.name === "Luxury Asset" && !assetData.legacies)
      newErrors.legacies = "Please Select Legacy.";
    if (assetType?.name === "Luxury Asset" && !assetData.itemType)
      newErrors.itemType = "Please Select items.";
    if (assetType?.name === "Company" && !assetData.companyAssetType)
      if (assetType?.name === "Pension") {
        // if (assetType?.name === "Pension" && !assetData.age)
        //   newErrors.ownershipType = "Please select the ownership Type.";
        const cleanValue = (val) =>
          parseFloat(val?.toString().replace(/'/g, "")) || 0;

        const r = cleanValue(assetData.retirement);
        const a = cleanValue(assetData.age);

        if (!assetData.age) newErrors.age = "Please enter your current age.";
        if (!assetData.retirement)
          newErrors.retirement = "Please enter your retirement age.";
        else if (r <= a) {
          newErrors.retirement =
            "Retirement age must be greater than current age.";
        }
      }
    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = validateFields();
    if (Object.keys(newErrors).length === 0) {
      onSubmit(assetData, assetType?.name);
    } else {
      setErrors(newErrors);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-semibold mb-6 text-lightPrimary dark:text-primary-gradient inline-block">
        General
      </h2>

      <form onSubmit={handleSubmit}>
        <div className="grid md:grid-cols-2 gap-5 text-primary mb-8">
          {assetType?.name === "Stock" && (
            <InputField
              label="ISIN*"
              placeholder="Enter your ISIN"
              name="isin"
              value={assetData.isin}
              onChange={handleInputChange}
              required={true}
              errors={errors.isin}
            />
          )}
          <InputField
            label="Name*"
            placeholder="Enter your asset's name"
            name="name"
            value={assetData.name}
            onChange={handleInputChange}
            required={true}
            errors={errors.name}
          />

          <FormDropdown
            label="Include In*"
            value={assetData.inclusion}
            name="inclusion"
            onChange={handleInputChange}
            options={groupsData}
            optionsLabel="name"
            error={errors.inclusion}
            className={`p-dropdown w-full bg-transparent border border-info dark:border-gray-300 rounded-lg ${
              assetData.inclusion ? "asset_Dropdown" : ""
            }`}
            containerClassName="col-span-2"
            labelClassName="block mb-2 text-lg text-info dark:text-grey font-semibold"
            mainClassName="w-full"
            placeholder="Select an included In"
          />
          <FormDropdown
            label="Asset Type*"
            value={assetData.assetType}
            name="assetType"
            onChange={handleInputChange}
            options={
              assetType?.name === "Cash"
                ? [assetTypeOption[4]]
                : assetType?.name === "Stock"
                ? assetTypeStockOption
                : assetType?.name === "Real Estate"
                ? assetTypeRealEstateOption
                : assetType?.name === "Pension"
                ? assetTypePensionOption
                : assetTypeOption
            }
            optionsLabel="name"
            error={errors.assetType}
            className={`w-full bg-transparent border border-info dark:border-gray-300 rounded-lg ${
              assetData.assetType ? "asset_Dropdown" : ""
            }`}
            containerClassName="col-span-2"
            labelClassName="block mb-2 text-lg text-info dark:text-grey font-semibold"
            mainClassName="w-full"
            placeholder="Select a asset type"
          />

          <FormDropdown
            label="Currency*"
            value={assetData.currency}
            name="currency"
            onChange={handleInputChange}
            options={currencyOption}
            optionsLabel="label"
            error={errors.currency}
            className={`w-full bg-transparent border border-info dark:border-gray-300 rounded-lg ${
              assetData.currency ? "asset_Dropdown" : ""
            }`}
            containerClassName="col-span-2"
            labelClassName="block mb-2 text-lg text-info dark:text-grey font-semibold"
            mainClassName="w-full"
            placeholder="Select a currency"
          />

          <FormDropdown
            label="Sector*"
            value={assetData.sector}
            name="sector"
            onChange={handleInputChange}
            options={sectors}
            optionsLabel="name"
            error={errors.sector}
            className={`w-full bg-transparent border border-info dark:border-gray-300 rounded-lg ${
              assetData.sector ? "asset_Dropdown" : ""
            }`}
            placeholder="Select a sector"
            containerClassName="col-span-2"
            labelClassName="block mb-2 text-lg text-info dark:text-grey font-semibold"
            mainClassName="w-full"
          />

          {assetType?.name === "Luxury Asset" && (
            <>
              <FormDropdown
                label="Legacy*"
                name="legacies"
                value={assetData.legacies}
                onChange={handleInputChange}
                options={legaciesData}
                optionsLabel="name"
                error={errors.legacies}
                className={`w-full bg-transparent border border-info dark:border-gray-300 rounded-lg ${
                  assetData.legacies ? "asset_Dropdown" : ""
                }`}
                placeholder="Select a legacy"
                containerClassName="col-span-2"
                labelClassName="block mb-2 text-lg text-info dark:text-grey font-semibold"
                mainClassName="w-full"
              />
              <FormDropdown
                label="Item Type"
                name="itemType"
                value={assetData.itemType}
                onChange={handleInputChange}
                options={itemType}
                optionsLabel="name"
                error={errors.itemType}
                className={`w-full bg-transparent border border-info dark:border-gray-300 rounded-lg ${
                  assetData.itemType ? "asset_Dropdown" : ""
                }`}
                placeholder="Select a item"
                containerClassName="col-span-2"
                labelClassName="block mb-2 text-lg text-info dark:text-grey font-semibold"
                mainClassName="w-full"
              />
            </>
          )}

          {assetType?.name === "Real Estate" && (
            <FormDropdown
              label="Linked With"
              value={assetData.linkedWith}
              showClear
              name="linkedWith"
              onChange={handleInputChange}
              options={linkedWith}
              optionsLabel="name"
              error={errors.linkedWith}
              className={`w-full bg-transparent border border-info dark:border-gray-300 rounded-lg ${
                assetData.linkedWith ? "asset_Dropdown" : ""
              }`}
              placeholder="Select a Linked With"
              containerClassName="col-span-2"
              labelClassName="block mb-2 text-lg text-info dark:text-grey font-semibold"
              mainClassName="w-full"
            />
          )}

          {assetType?.name === "Company" && (
            <>
              <FormDropdown
                label="Company Asset Type*"
                value={assetData.companyAssetType}
                name="companyAssetType"
                onChange={handleInputChange}
                options={companyAssetTypeOption}
                optionsLabel="name"
                error={errors.companyAssetType}
                className={`w-full bg-transparent border border-info dark:border-gray-300 rounded-lg ${
                  assetData.companyAssetType ? "asset_Dropdown" : ""
                }`}
                placeholder="Select a Company Asset Type"
                containerClassName="col-span-2"
                labelClassName="block mb-2 text-lg text-info dark:text-grey font-semibold"
                mainClassName="w-full"
              />
              <NumberFieldBorder
                label="Ownership*"
                placeholder="Enter your Ownership"
                name="ownership"
                value={assetData.ownership}
                onChange={(e) => {
                  const newValue = e.target.value;
                  if (
                    newValue === "" ||
                    (Number(newValue) >= 0 && Number(newValue) <= 100)
                  ) {
                    handleInputChange(e);
                  }
                }}
                required={false}
                errors={errors.ownership}
              />

              <FormDropdown
                label="Ownership Type*"
                value={assetData.ownershipType}
                name="ownershipType"
                onChange={handleInputChange}
                options={owernershipTypeOption}
                optionsLabel="name"
                placeholder="Select a Ownership Type"
                error={errors.ownershipType}
                className={`w-full bg-transparent border border-info dark:border-gray-300 rounded-lg ${
                  assetData.ownershipType ? "asset_Dropdown" : ""
                }`}
                containerClassName="col-span-2"
                labelClassName="block mb-2 text-lg text-info dark:text-grey font-semibold"
                mainClassName="w-full"
              />
            </>
          )}

          {assetType?.name === "Other" && (
            <NumberFieldBorder
              label="Commitment"
              placeholder="Enter Commitment"
              name="commitment"
              value={assetData.commitment}
              onChange={handleInputChange}
              required={false}
              errors={errors.commitment}
            />
          )}
          {assetType?.name == "Stock" && (
            <>
              <FormDropdown
                label="Symbol*"
                value={assetData.symbol}
                name="symbol"
                onChange={handleInputChange}
                options={stockSymbol}
                optionsLabel={(option) =>
                  `${option.companyName} - ${option.exchangeShortName}`
                }
                placeholder="Select a symbol"
                error={errors.symbol}
                className={`w-full bg-transparent border border-info dark:border-gray-300 rounded-lg ${
                  assetData.symbol ? "asset_Dropdown" : ""
                }`}
                containerClassName="col-span-2"
                labelClassName="block mb-2 text-lg text-info dark:text-grey font-semibold"
                mainClassName="w-full"
              />

              <FormDropdown
                label="Cash Assets"
                value={assetData.cashAssets}
                showClear
                name="cashAssets"
                onChange={handleInputChange}
                options={userAssets}
                optionsLabel="name"
                error={errors.symbol}
                placeholder="Select a cash assets"
                className={`w-full bg-transparent border border-info dark:border-gray-300 rounded-lg ${
                  assetData.cashAssets ? "asset_Dropdown" : ""
                }`}
                containerClassName="col-span-2"
                labelClassName="block mb-2 text-lg text-info dark:text-grey  font-semibold"
                mainClassName="w-full"
              />
            </>
          )}
        </div>

        <h2 className="text-2xl font-semibold mb-6 text-lightPrimary dark:text-primary-gradient inline-block">
          Transaction
        </h2>
        <div
          className={`grid ${
            assetType?.name === "Stock"
              ? "md:grid-cols-3 grid-cols-1"
              : assetType?.name === "Pension"
              ? ""
              : "md:grid-cols-2 grid-cols-1"
          } gap-5 text-primary mb-8`}
        >
          {assetType?.name !== "Pension" && (
            <div className="flex flex-col">
              <div className="w-full flex space-x-4">
                <label
                  htmlFor="date"
                  className="block text-base font-medium text-info dark:text-grey font-semibold text-nowrap"
                >
                  Date*
                </label>
                <div className="flex-1">
                  <Calendar
                    value={assetData.date}
                    onChange={(e) => handleInputChange(e.value, "date")}
                    onHide={() => {
                      const input = document.querySelector(".asset_date input");
                      if (input) input.blur();
                    }}
                    dateFormat="dd/mm/yy"
                    className="asset_date w-full"
                    maxDate={new Date()}
                  />
                  {errors.date && (
                    <small className="text-red-500">{errors.date}</small>
                  )}
                </div>
              </div>
            </div>
          )}

          {assetType?.name !== "Stock" && assetType?.name !== "Pension" && (
            <InputFieldWithOutBorder
              label={
                assetType?.name == "Real Estate"
                  ? "Initial Purchase*"
                  : "Initial Balance*"
              }
              placeholder="Enter your initial balance"
              name="initialBalance"
              value={assetData.initialBalance}
              onChange={(e) => handleNumericChange(e, "initialBalance")}
              required={true}
              errors={errors.initialBalance}
            />
          )}

          {assetType?.name === "Stock" && (
            <>
              <NumberField
                label="Quantity*"
                placeholder="Enter quantity"
                name="quantity"
                value={assetData.quantity}
                onChange={handleInputChange}
                required={true}
                errors={errors.quantity}
              />

              <InputFieldWithOutBorder
                label="Cost price*"
                placeholder="Enter cost price"
                name="costPrice"
                value={assetData.costPrice}
                onChange={(e) => handleNumericChange(e, "costPrice")}
                required={true}
                errors={errors.costPrice}
              />
            </>
          )}

          {assetType?.name === "Real Estate" && (
            <>
              <NumberField
                label="NOI*"
                placeholder="Enter NOI"
                name="noi"
                value={assetData.noi}
                onChange={handleInputChange}
                required={true}
                errors={errors.noi}
              />
              <NumberField
                label="Cap Rate*"
                placeholder="Enter Cap Rate"
                name="capRate"
                value={assetData.capRate}
                onChange={handleInputChange}
                required={true}
                errors={errors.capRate}
              />
            </>
          )}

          {assetType?.name === "Company" && (
            <>
              <InputFieldWithOutBorder
                label="EBITDA*"
                placeholder="Enter EBITDA"
                name="ebitda"
                value={assetData.ebitda}
                onChange={(e) => handleNumericChange(e, "ebitda")}
                required={true}
                errors={errors.ebitda}
              />

              <InputFieldWithOutBorder
                label="Multiples*"
                placeholder="Enter Multiples"
                name="multiples"
                value={assetData.multiples}
                onChange={(e) => handleNumericChange(e, "multiples")}
                required={true}
                errors={errors.multiples}
              />
            </>
          )}

          {assetType?.name === "Pension" && (
            <>
              <div className="text-white rounded-lg grid grid-cols-1 lg:grid-cols-3 gap-8 text-base font-semibold">
                <div className="space-y-5 pt-[44px]">
                  <InputBorderBottomBorder
                    label="Age"
                    placeholder="Enter Age"
                    name="age"
                    value={assetData.age}
                    onChange={(e) => handleNumericChange(e, "age")}
                    containerClassName="border-b border-info dark:border-gray-600 focus:border-b-2 hover:border-b-2 flex"
                    fieldClassName="w-full text-end text-base dark:text-gray-400 text-dark font-extralight bg-transparent border-0 focus:outline-none"
                    errors={errors.age}
                  />
                  <InputBorderBottomBorder
                    label="Retirement"
                    placeholder="Enter Retirement"
                    name="retirement"
                    value={assetData.retirement}
                    onChange={(e) => handleNumericChange(e, "retirement")}
                    containerClassName="border-b border-info dark:border-gray-600 focus:border-b-2 hover:border-b-2 flex"
                    fieldClassName="w-full text-end text-base dark:text-gray-400 text-dark font-extralight bg-transparent border-0 focus:outline-none"
                    errors={errors.retirement}
                  />
                </div>
                <div className="space-y-5">
                  <InputBorderBottomBorder
                    label="Growth"
                    placeholder="Growth"
                    name="growth"
                    value={assetData.growth}
                    onChange={(e) => handleNumericChange(e, "growth")}
                    containerClassName="border-0 border-info dark:border-gray-600 focus:border-b-2 hover:border-b-2 flex items-center"
                    fieldClassName="w-full text-end text-xs dark:text-gray-400 text-dark font-extralight bg-transparent border-0 focus:outline-none"
                    labelClassName="text-xs"
                    errors={errors.growth}
                  />
                  <InputBorderBottomBorder
                    label="Gross Salary"
                    placeholder="Enter Gross Salary"
                    name="growthGrossSalary"
                    value={assetData.growthGrossSalary}
                    onChange={(e) =>
                      handleNumericChange(e, "growthGrossSalary")
                    }
                    containerClassName="border-b border-info dark:border-gray-600 focus:border-b-2 hover:border-b-2 flex"
                    fieldClassName="w-full text-end text-base dark:text-gray-400 text-dark font-extralight bg-transparent border-0 focus:outline-none"
                    errors={errors.growthGrossSalary}
                  />
                  <InputBorderBottomBorder
                    label="Annual Return"
                    placeholder="Enter Annual Return"
                    name="annualReturn"
                    value={assetData.annualReturn}
                    onChange={(e) => handleNumericChange(e, "annualReturn")}
                    containerClassName="border-b border-info dark:border-gray-600 focus:border-b-2 hover:border-b-2 flex"
                    fieldClassName="w-full text-end text-base dark:text-gray-400 text-dark font-extralight bg-transparent border-0 focus:outline-none"
                    errors={errors.annualReturn}
                    suffix="%"
                  />
                </div>
                <div className="space-y-5">
                  <InputBorderBottomBorder
                    label="Maturity"
                    placeholder="Maturity"
                    name="maturity"
                    disabled={true}
                    value={assetData.maturity}
                    onChange={(e) => handleNumericChange(e, "maturity")}
                    containerClassName="border-0 border-info dark:border-gray-600 focus:border-b-2 hover:border-b-2 flex items-center"
                    fieldClassName="disabled:cursor-not-allowed w-full text-end text-xs dark:text-gray-400 text-dark font-extralight bg-transparent border-0 focus:outline-none"
                    labelClassName="text-xs"
                    errors={errors.maturity}
                  />
                  <InputBorderBottomBorder
                    label="Expected Gross Salary"
                    placeholder="Enter Gross Salary"
                    name="maturityGrossSalary"
                    value={assetData.maturityGrossSalary}
                    onChange={(e) =>
                      handleNumericChange(e, "maturityGrossSalary")
                    }
                    containerClassName="border-b border-info dark:border-gray-600 focus:border-b-2 hover:border-b-2 flex"
                    fieldClassName="w-full text-end text-base dark:text-gray-400 text-dark font-extralight bg-transparent border-0 focus:outline-none"
                    labelClassName="text-base"
                    errors={errors.maturityGrossSalary}
                  />
                </div>
              </div>

              {[
                {
                  label: "1st Piller",
                  fields: [
                    {
                      name: "AVSContribution",
                      label: "AVS Contribution",
                      placeholder: "Enter AVS contribution",
                      suffix: "%",
                    },
                    {
                      name: "contributionYear",
                      label: "Contribution Year",
                      placeholder: "Enter Contribution Year",
                    },
                  ],
                },
                {
                  label: "2nd Piller",
                  fields: [
                    {
                      name: "contributionRate",
                      label: "Contribution Rate",
                      placeholder: "Enter Contribution Rate",
                      suffix: "%",
                    },
                    {
                      name: "secondPillarAverageIncome",
                      label: "Average income",
                      placeholder: "Enter Average income",
                      disabled: true,
                    },
                    {
                      name: "conversionRate",
                      label: "Conversion rate",
                      placeholder: "Enter Conversion Rate",
                      suffix: "%",
                    },
                  ],
                },
                {
                  label: "3rd Piller",
                  fields: [
                    {
                      name: "futureContribution3rdPillar",
                      label: "Future Contribution to 3rd Pillar",
                      placeholder: "Enter Future Contribution to 3rd Pillar",
                    },
                    // {
                    //   name: "thirdPillarAverageIncome",
                    //   label: "Average income",
                    //   placeholder: "Enter Average income",
                    //   disabled: true,
                    // },
                  ],
                },
              ].map((section, index) => (
                <div
                  key={index}
                  className="text-white rounded-lg grid grid-cols-1 lg:grid-cols-3 gap-8 md:mt-5 text-base font-semibold relative"
                >
                  <span className="absolute left-[-6.5rem] top-[0.4rem] text-xs text-info dark:text-gray-400">
                    {section.label}
                  </span>
                  {section.fields.map((field, i) => (
                    <InputBorderBottomBorder
                      key={i}
                      label={field.label}
                      placeholder={field.placeholder}
                      name={field.name}
                      value={assetData[field.name]}
                      disabled={field.disabled}
                      onChange={(e) =>
                        handleDynamicNumericChange(e, field.name)
                      }
                      suffix={field.suffix}
                      containerClassName="border-b border-info dark:border-gray-600 focus:border-b-2 hover:border-b-2 flex"
                      fieldClassName="w-full disabled:cursor-not-allowed text-end text-base dark:text-gray-400 text-dark font-extralight bg-transparent border-0 focus:outline-none"
                      errors={errors[field.name]}
                    />
                  ))}
                </div>
              ))}

              <div className="text-white rounded-lg grid grid-cols-1 lg:grid-cols-3 gap-8 text-base font-semibold">
                <InputBorderBottomBorder
                  label="Past Contribution 3rd Pillar"
                  placeholder="Enter Past Contribution 3rd Pillar"
                  name="pastContribution3rdPillar"
                  value={assetData.pastContribution3rdPillar}
                  onChange={(e) =>
                    handleNumericChange(e, "pastContribution3rdPillar")
                  }
                  containerClassName="border-b border-info dark:border-gray-600 focus:border-b-2 hover:border-b-2 flex"
                  fieldClassName="w-full text-end font-extralight  text-base dark:text-gray-400 text-dark bg-transparent border-0 focus:outline-none"
                  errors={errors.pastCotisation3rdPillar}
                />
              </div>
            </>
          )}
        </div>
        <div className="flex md:justify-end justify-center">
          <button
            type="submit"
            className="px-6 py-2 dark:text-black border border-dark text-sm font-medium bg-[radial-gradient(circle_at_10%_10%,rgb(237,237,237),#9e9e9e,#9e9e9e,#9e9e9e)] hover:text-white text-dark dark:button-primary-gradient hover:bg-secondary rounded-lg"
          >
            Submit
          </button>
        </div>
      </form>
    </div>
  );
};

export default AssetForm;
